import Radio from "@mui/material/Radio";
import type { HTMLProps, ReactNode } from "react";
import { memo } from "react";
import { classList } from "~lib/classList.ts";

import styles from "./SelectedItem.module.scss";

export interface SelectedItemProps
  extends Omit<HTMLProps<HTMLDivElement>, "ref" | "children"> {
  variant?: "default" | "groupHeader" | "groupItem";
  deselected?: boolean;
  children: ReactNode;
  actions?: ReactNode;
  info?: ReactNode;
  color?: "primary" | "secondary";
}

function SelectedItem({
  variant = "default",
  color = "primary",
  deselected = false,
  children,
  actions,
  info,
  className,
  ...props
}: SelectedItemProps) {
  return (
    <div
      className={classList(
        styles.container,
        styles[`variant_${variant}`],
        styles[`color_${color}`],
        deselected && styles.deselected,
        className,
      )}
      {...props}
    >
      <div className={styles.title}>{children}</div>
      <div className={styles.info}>
        {info}
        {actions}
      </div>
    </div>
  );
}

interface CheckableSelectedItemProps extends SelectedItemProps {
  id?: string;
  checked?: boolean;
  onChangeChecked?: (...event: unknown[]) => void;
}

export function CheckableSelectedItem({
  checked,
  onChangeChecked,
  id,
  ...props
}: CheckableSelectedItemProps) {
  return (
    <SelectedItem
      {...props}
      className={classList(
        styles.selectableContainer__item,
        checked && styles.checked,
      )}
    >
      {onChangeChecked && (
        <Radio
          id={id}
          checked={Boolean(checked)}
          onChange={onChangeChecked}
          className={styles.selectableContainer__radio}
        />
      )}
      <label htmlFor={id} className={styles.selectableContainer__label}>
        {props.children}
      </label>
    </SelectedItem>
  );
}

const MemoizedSelectItem = memo(SelectedItem);

export { MemoizedSelectItem as SelectedItem };
